/**
 * Shared styles for Profile Detail tabs
 * Based on AddProfileScreen design system
 */

import { StyleSheet } from 'react-native';
import colors from './colors';

export const profileDetailStyles = StyleSheet.create({
  // Container styles
  container: {
    flex: 1,
    backgroundColor: colors.primary,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 100,
  },
  
  // Loading states
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  loadingText: {
    color: colors.white,
    marginTop: 10,
    fontSize: 16,
  },
  
  // Section headers (matching AddProfile)
  sectionHeader: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.white,
    marginTop: 20,
    marginBottom: 16,
  },
  
  // Input wrapper with floating label (matching AddProfile)
  inputWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  floatingLabel: {
    position: 'absolute',
    top: -10,
    left: 12,
    backgroundColor: colors.primary,
    paddingHorizontal: 4,
    fontSize: 12,
    fontWeight: '500',
    color: colors.secondary,
    zIndex: 1,
  },
  
  // Input fields (matching AddProfile)
  input: {
    borderWidth: 1,
    borderColor: colors.secondary,
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    backgroundColor: '#1e3a52',
    fontSize: 14,
    color: colors.white,
    minHeight: 48,
  },
  inputFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  inputError: {
    borderColor: '#EF4444',
    borderWidth: 2,
  },
  errorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
    marginLeft: 4,
  },
  inputMultiline: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  
  // Dropdown styles (matching AddProfile)
  dropdown: {
    borderWidth: 1,
    borderColor: colors.secondary,
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    backgroundColor: '#1e3a52',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 48,
  },
  dropdownText: {
    fontSize: 14,
    color: colors.white,
  },
  dropdownPlaceholder: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  
  // View mode container (matching edit mode styling)
  viewModeContainer: {
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    backgroundColor: 'transparent',
    minHeight: 48,
    justifyContent: 'center',
  },
  viewModeText: {
    fontSize: 14,
    color: '#9CA3AF',
    lineHeight: 20,
  },
  
  // Display mode (view only)
  infoItem: {
    marginBottom: 16,
  },
  infoLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 4,
    fontWeight: '500',
  },
  infoValue: {
    fontSize: 14,
    color: colors.white,
    paddingVertical: 8,
  },
  infoValueEmpty: {
    fontSize: 14,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  
  // Grid layout (responsive)
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  
  // Floating action buttons
  floatingButtonContainer: {
    position: 'absolute',
    top: 15,
    right: 15,
    flexDirection: 'row',
    gap: 8,
    zIndex: 1000,
  },
  floatingButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  floatingEditButton: {
    backgroundColor: colors.secondary,
  },
  floatingSaveButton: {
    backgroundColor: colors.success,
  },
  floatingCancelButton: {
    backgroundColor: colors.error,
  },
  
  // Modal styles (matching AddProfile)
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '70%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  modalScroll: {
    maxHeight: 400,
  },
  modalItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalItemText: {
    fontSize: 16,
    color: colors.text,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalDoneButton: {
    backgroundColor: colors.secondary,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  modalDoneButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  
  // Card styles (for Education tab)
  card: {
    borderWidth: 1,
    borderColor: colors.accent,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    backgroundColor: '#1e3a52',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 14,
    color: '#9CA3AF',
  },
  
  // Empty state
  emptyStateContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  emptyStateText: {
    fontSize: 15,
    color: '#9CA3AF',
    textAlign: 'center',
    marginTop: 15,
    marginBottom: 20,
  },
  noDataText: {
    fontSize: 15,
    color: colors.white,
    textAlign: 'center',
    marginTop: 20,
  },
  
  // Action buttons
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.secondary,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    justifyContent: 'center',
  },
  buttonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  buttonSecondary: {
    backgroundColor: colors.textSecondary,
  },
});

export default profileDetailStyles;
